import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { useLanguage } from '../LanguageContext';
import { useSiteStats } from '../SiteStatsContext';
import { useAuth } from '../AuthContext';
import LanguageSelector from './LanguageSelector';
import { LogOut, User, Package, Bell, ChevronDown, Shield, X, Megaphone, AlertTriangle, CheckCircle, Tag, Menu, Home, Gift, TrendingUp, Wrench, Key, Monitor, BarChart3, HelpCircle, Star, Lightbulb } from 'lucide-react';

// Composant d'icône Discord optimisé
const DiscordIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 127.14 96.36" fill="currentColor">
    <g>
      <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/>
    </g>
  </svg>
);

const Header = ({ onMenuClick }) => {
  const { t } = useLanguage();
  const siteStats = useSiteStats();  const { user, isAuthenticated, loginWithDiscord, logout, token } = useAuth();
  const [stats, setStats] = useState({ online: 23, total: 1300 });
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAffiliate, setIsAffiliate] = useState(false);
  const [isHelper, setIsHelper] = useState(false);
  const [hasLicence, setHasLicence] = useState(false);
  const [helperNotifs, setHelperNotifs] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [readAnnouncements, setReadAnnouncements] = useState([]);
  const [referralPoints, setReferralPoints] = useState(0);
  const dropdownRef = useRef(null);
  const notifRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Grouped navigation menus (dropdowns)
  const NAV_MENUS = [
    {
      label: t('nav.services'),
      items: [
        { to: '/services', label: t('nav.services'), desc: 'Windows · BIOS · Pack', icon: Monitor },
        { to: '/benchmark', label: t('nav.benchmark'), desc: 'Gains de performance', icon: BarChart3 },
        { to: '/faq', label: t('nav.faq'), desc: 'Questions fréquentes', icon: HelpCircle },
      ],
    },
    {
      label: 'Communauté',
      items: [
        { to: '/parrainage', label: t('sidebar.parrainage'), desc: 'Gagne des récompenses', icon: Gift },
        { to: '/feedbacks', label: t('nav.reviews'), desc: 'Avis de nos clients', icon: Star },
        { to: '/suggestions', label: 'Suggestions', desc: 'Propose une idée', icon: Lightbulb },
      ],
    },
  ];

  // Load read announcements from localStorage
  useEffect(() => {
    const read = JSON.parse(localStorage.getItem('readAnnouncements') || '[]');
    setReadAnnouncements(read);
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_URL}/api/discord/stats`);
        if (response.ok) {
          const data = await response.json();
          setStats({
            online: data.online_members || 23,
            total: data.total_members || 1300
          });
        }
      } catch (error) {
        console.log('Using default stats');
      }
    };
    
    fetchStats();
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, [API_URL]);

  // Fetch announcements
  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const response = await fetch(`${API_URL}/api/announcements`);
        if (response.ok) {
          const data = await response.json();
          setAnnouncements(data);
        }
      } catch (error) {
        console.log('Could not fetch announcements');
      }
    };
    
    fetchAnnouncements();
    const interval = setInterval(fetchAnnouncements, 30000);
    return () => clearInterval(interval);
  }, [API_URL]);

  // Check if user is admin
  useEffect(() => {
    if (isAuthenticated && token) {
      const checkAdmin = async () => {
        try {
          const response = await fetch(`${API_URL}/api/admin/check`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await response.json();
          setIsAdmin(data.is_admin);
        } catch (error) {
          setIsAdmin(false);
        }
      };
      checkAdmin();
    }
  }, [isAuthenticated, token, API_URL]);

  // Check if user has a licence (re-checked when dropdown opens for fresh data)
  useEffect(() => {
    if (!isAuthenticated || !token) { setHasLicence(false); return; }
    const fetchLicence = async () => {
      try {
        const r = await fetch(`${API_URL}/api/user/licence-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const d = await r.json();
        setHasLicence(d.has_licence === true);
      } catch {
        setHasLicence(false);
      }
    };
    fetchLicence();
  }, [isAuthenticated, token, API_URL, showDropdown]);

  // Check if user is affiliate
  useEffect(() => {
    if (isAuthenticated && token) {
      const checkAffiliate = async () => {
        try {
          const response = await fetch(`${API_URL}/api/my-affiliate-stats`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            setIsAffiliate(true);
          } else {
            setIsAffiliate(false);
          }
        } catch (error) {
          setIsAffiliate(false);
        }
      };
      checkAffiliate();
    }
  }, [isAuthenticated, token, API_URL]);

  // Check if user is helper + fetch helper notifications
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    const checkHelper = async () => {
      try {
        const [helperRes, notifRes] = await Promise.all([
          fetch(`${API_URL}/api/helper-space/status`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_URL}/api/notifications/my`, { headers: { 'Authorization': `Bearer ${token}` } })
        ]);
        if (helperRes.ok) {
          const d = await helperRes.json();
          setIsHelper(d.is_helper);
        }
        if (notifRes.ok) {
          const notifs = await notifRes.json();
          setHelperNotifs(notifs.filter(n => !n.read));
        }
      } catch (e) {}
    };
    checkHelper();
    const interval = setInterval(checkHelper, 15000);
    return () => clearInterval(interval);
  }, [isAuthenticated, token, API_URL]);

  const markHelperNotifRead = async (notifId) => {
    try {
      await fetch(`${API_URL}/api/notifications/read/${notifId}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      setHelperNotifs(prev => prev.filter(n => n.id !== notifId));
    } catch (e) {}
  };

  // Fetch referral points
  useEffect(() => {
    if (isAuthenticated && token) {
      const fetchReferralPoints = async () => {
        try {
          const response = await fetch(`${API_URL}/api/referral/stats`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setReferralPoints(data.total_points || 0);
          }
        } catch (error) {
          console.error('Error fetching referral points:', error);
        }
      };
      fetchReferralPoints();
      // Refresh every 30 seconds
      const interval = setInterval(fetchReferralPoints, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, token, API_URL]);


  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = (id) => {
    const newRead = [...readAnnouncements, id];
    setReadAnnouncements(newRead);
    localStorage.setItem('readAnnouncements', JSON.stringify(newRead));
  };

  const markAllAsRead = () => {
    const allIds = announcements.map(a => a.id);
    setReadAnnouncements(allIds);
    localStorage.setItem('readAnnouncements', JSON.stringify(allIds));
  };

  const unreadCount = announcements.filter(a => !readAnnouncements.includes(a.id)).length;

  const getAvatarUrl = () => {
    if (user?.avatar) {
      return `https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.png`;
    }
    return null;
  };

  const getAnnouncementIcon = (type) => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'promo':
        return <Tag className="w-4 h-4 text-purple-400" />;
      default:
        return <Megaphone className="w-4 h-4 text-cyan-400" />;
    }
  };

  const getAnnouncementBg = (type) => {
    switch (type) {
      case 'warning':
        return 'border-l-yellow-500 bg-yellow-500/5';
      case 'success':
        return 'border-l-green-500 bg-green-500/5';
      case 'promo':
        return 'border-l-purple-500 bg-purple-500/5';
      default:
        return 'border-l-cyan-500 bg-cyan-500/5';
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR');
  };
  
  return (
    <header className="fixed top-0 right-0 left-0 lg:left-52 h-14 lg:h-16 bg-[#0a0b14]/95 backdrop-blur-sm border-b border-cyan-900/30 z-30">
      <div className="h-full px-2 sm:px-3 lg:px-6 flex items-center justify-between">
        {/* Mobile Menu Button + Logo */}
        <div className="flex items-center gap-2 sm:gap-3">
          <button 
            onClick={onMenuClick}
            className="lg:hidden p-1.5 text-gray-400 hover:text-white"
            data-testid="hamburger-menu-btn"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-1.5">
            <img 
              src="/aqmopti-logo.png"
              alt="AqmOpti" 
              className="w-7 h-7 object-contain rounded-lg"
              style={{ imageRendering: 'crisp-edges', mixBlendMode: 'lighten' }}
            />
            <span className="text-cyan-400 font-bold text-xs">AqmOpti</span>
          </div>

          {/* Desktop Navigation Links — dropdown style */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="main-nav">
            {/* Accueil (simple link) */}
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `px-3.5 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  isActive ? 'text-cyan-400' : 'text-gray-300 hover:text-white hover:bg-white/[0.05]'
                }`
              }
            >
              {t('nav.home')}
            </NavLink>

            {NAV_MENUS.map((menu) => {
              const active = menu.items.some(
                (it) => location.pathname === it.to || (it.to !== '/' && location.pathname.startsWith(it.to))
              );
              return (
                <div key={menu.label} className="relative group">
                  <button
                    className={`flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                      active ? 'text-cyan-400 bg-white/[0.04]' : 'text-gray-300 hover:text-white group-hover:bg-white/[0.05]'
                    }`}
                    data-testid={`nav-menu-${menu.label.toLowerCase()}`}
                  >
                    {menu.label}
                    <ChevronDown className="w-3.5 h-3.5 text-gray-500 transition-transform duration-300 group-hover:-rotate-180 group-hover:text-cyan-400" />
                  </button>

                  {/* Dropdown panel (CSS hover, bridged by pt-2) */}
                  <div className="absolute left-0 top-full pt-2 opacity-0 invisible translate-y-1 transition-all duration-200 ease-out group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 z-50">
                    <div className="w-64 bg-zinc-950/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.85)] p-1.5">
                      {menu.items.map((it) => (
                        <NavLink
                          key={it.to}
                          to={it.to}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-200 hover:bg-cyan-500/10 hover:text-cyan-300 transition-colors duration-150"
                          data-testid={`nav-item-${it.to.replace(/\//g, '') || 'home'}`}
                        >
                          <span className="w-8 h-8 rounded-lg bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center flex-shrink-0">
                            <it.icon className="w-4 h-4 text-cyan-300" strokeWidth={2.2} />
                          </span>
                          <span className="min-w-0">
                            <span className="block text-[13px] font-semibold leading-tight">{it.label}</span>
                            <span className="block text-[11px] text-gray-500 leading-tight mt-0.5">{it.desc}</span>
                          </span>
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </nav>
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-1 sm:gap-2 lg:gap-4">
          {/* Social Networks - Hidden on mobile */}
          <div className="hidden md:flex items-center gap-2">
            <a 
              href="https://twitter.com/aqmopti" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900 border border-cyan-900/30 hover:border-cyan-500/50 hover:bg-cyan-950/30 hover:scale-110 transition-all duration-200 group"
              title="Twitter/X"
            >
              <svg className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" viewBox="0 0 24 24" fill="currentColor" style={{ shapeRendering: 'geometricPrecision' }}>
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            <a 
              href="https://www.tiktok.com/@aqmoptimisation" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900 border border-cyan-900/30 hover:border-cyan-500/50 hover:bg-cyan-950/30 hover:scale-110 transition-all duration-200 group"
              title="TikTok"
            >
              <svg className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" viewBox="0 0 24 24" fill="currentColor" style={{ shapeRendering: 'geometricPrecision' }}>
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </a>
            <a 
              href="https://discord.gg/aqmopti" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900 border border-cyan-900/30 hover:border-cyan-500/50 hover:bg-cyan-950/30 hover:scale-110 transition-all duration-200 group"
              title="Discord"
            >
              <DiscordIcon className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
            </a>
          </div>

          {/* Trust pill — clients + rating (hidden on small screens to save room) */}
          <div
            className="hidden md:flex items-center gap-1.5 lg:gap-2 px-2 lg:px-3 py-1 lg:py-1.5 rounded-full bg-gradient-to-r from-amber-500/10 via-amber-400/5 to-cyan-500/10 border border-amber-400/25 shadow-[0_0_18px_-6px_rgba(245,158,11,0.35)] hover:border-amber-300/40 transition-colors"
            data-testid="trust-pill"
            title="Notre communauté"
          >
            <svg className="w-3 h-3 lg:w-3.5 lg:h-3.5 text-amber-300 drop-shadow-[0_0_4px_rgba(252,211,77,0.6)]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
            </svg>
            <span className="text-[10px] lg:text-xs font-semibold text-amber-100 tabular-nums">+{siteStats.optimizedPCs}</span>
            <span className="hidden lg:inline text-[10px] lg:text-xs text-amber-200/80 font-medium">clients</span>
            <span className="text-amber-300/40 text-[10px] lg:text-xs hidden lg:inline">·</span>
            <span className="hidden lg:inline text-[10px] lg:text-xs font-semibold text-amber-100 tabular-nums">{siteStats.rating}<span className="text-amber-300/70">/5</span></span>
          </div>

          {/* Online Stats - Compact on mobile */}
          <div className="flex items-center gap-1 px-1.5 sm:px-2 lg:px-3 py-1 lg:py-1.5 bg-zinc-900/50 rounded-lg border border-cyan-900/20">
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-[9px] sm:text-xs text-green-400 font-medium">{stats.online}</span>
            <span className="text-gray-600 text-[9px] sm:text-[10px]">|</span>
            <span className="text-[9px] sm:text-xs text-white font-medium">{stats.total}</span>
          </div>

          {/* Language Selector - Always visible */}
          <LanguageSelector />

          {/* Notification Bell (when logged in) */}
          {isAuthenticated && (
            <div className="relative" ref={notifRef}>
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg bg-zinc-900 border border-cyan-900/30 hover:border-cyan-500/50 hover:bg-cyan-950/30 transition-all duration-200 group relative"
                data-testid="notifications-btn"
              >
                <Bell className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 group-hover:text-white transition-colors" />
                {(unreadCount + helperNotifs.length) > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-[0_0_8px_rgba(239,68,68,0.6)] ring-2 ring-zinc-900 animate-pulse">
                    {(unreadCount + helperNotifs.length) > 9 ? '9+' : (unreadCount + helperNotifs.length)}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-72 sm:w-80 bg-zinc-900 border border-cyan-900/30 rounded-xl shadow-2xl overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-cyan-900/20 flex items-center justify-between">
                    <h3 className="text-white font-semibold flex items-center gap-2 text-sm">
                      <Bell className="w-4 h-4 text-cyan-400" />
                      Notifications
                    </h3>
                    {unreadCount > 0 && (
                      <button 
                        onClick={markAllAsRead}
                        className="text-xs text-gray-400 hover:text-white transition-colors"
                      >
                        Tout lu
                      </button>
                    )}
                  </div>

                  <div className="max-h-64 sm:max-h-80 overflow-y-auto">
                    {/* Helper notifications (prioritaires) */}
                    {helperNotifs.map((notif) => (
                      <div key={notif.id}
                        className={`px-3 py-2.5 border-b border-cyan-900/10 border-l-2 cursor-pointer transition-colors
                          ${notif.type === 'helper_approved' ? 'border-l-green-500 bg-green-500/5 hover:bg-green-500/10' : 'border-l-red-500 bg-red-500/5 hover:bg-red-500/10'}`}
                        onClick={() => markHelperNotifRead(notif.id)}
                      >
                        <div className="flex items-start gap-2">
                          <div className="mt-0.5 flex-shrink-0">
                            {notif.type === 'helper_approved'
                              ? <CheckCircle className="w-4 h-4 text-green-400" />
                              : <X className="w-4 h-4 text-red-400" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1">
                              <h4 className="text-xs font-semibold text-white truncate">{notif.title}</h4>
                              <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full flex-shrink-0"></span>
                            </div>
                            <p className="text-[11px] text-gray-300 mt-0.5 line-clamp-2">{notif.message}</p>
                            {notif.type === 'helper_approved' && (
                              <button onClick={(e) => { e.stopPropagation(); navigate('/espace-helper'); markHelperNotifRead(notif.id); setShowNotifications(false); }}
                                className="text-[11px] text-cyan-400 hover:text-cyan-300 mt-1 underline">
                                Accéder à l'Espace Helper →
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    {announcements.length === 0 && helperNotifs.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <Bell className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                        <p className="text-gray-500 text-sm">Aucune notification</p>
                      </div>
                    ) : (
                      announcements.map((announcement) => {
                        const isRead = readAnnouncements.includes(announcement.id);
                        return (
                          <div 
                            key={announcement.id}
                            className={`px-3 py-2 border-b border-cyan-900/10 border-l-2 ${getAnnouncementBg(announcement.type)} ${!isRead ? 'bg-cyan-950/20' : ''} hover:bg-cyan-950/30 transition-colors cursor-pointer`}
                            onClick={() => markAsRead(announcement.id)}
                          >
                            <div className="flex items-start gap-2">
                              <div className="mt-0.5 flex-shrink-0">
                                {getAnnouncementIcon(announcement.type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1">
                                  <h4 className={`text-xs font-medium truncate ${!isRead ? 'text-white' : 'text-gray-300'}`}>
                                    {announcement.title}
                                  </h4>
                                  {!isRead && (
                                    <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full flex-shrink-0"></span>
                                  )}
                                </div>
                                <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-2">
                                  {announcement.content}
                                </p>
                                <p className="text-[10px] text-gray-500 mt-0.5">
                                  {formatDate(announcement.created_at)}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Login/User Profile */}
          {isAuthenticated ? (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center gap-1 sm:gap-2 px-1.5 sm:px-3 py-1 sm:py-1.5 bg-zinc-900/80 border border-cyan-900/30 rounded-full hover:border-cyan-500/50 transition-all duration-200"
                data-testid="profile-dropdown-btn"
              >
                {getAvatarUrl() ? (
                  <img 
                    src={getAvatarUrl()} 
                    alt={user.username} 
                    className="w-6 h-6 sm:w-8 sm:h-8 rounded-full border border-cyan-500/50" 
                  />
                ) : (
                  <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-cyan-600/30 flex items-center justify-center">
                    <User className="w-3 h-3 sm:w-4 sm:h-4 text-cyan-400" />
                  </div>
                )}
                <span className="hidden sm:block text-sm text-white font-medium max-w-[80px] truncate">{user.username}</span>
                <ChevronDown className={`w-3 h-3 sm:w-4 sm:h-4 text-gray-400 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {showDropdown && (
                <div className="absolute right-0 mt-2 w-72 sm:w-80 max-w-[calc(100vw-1rem)] bg-zinc-950/95 backdrop-blur-xl border border-white/[0.08] rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)] overflow-hidden z-50">
                  {/* User Info */}
                  <div className="px-5 pt-5 pb-4">
                    <p className="text-white font-semibold text-[15px] truncate leading-tight">{user.username}</p>
                    <p className="text-xs text-gray-500 truncate mt-0.5">{user.email || 'Discord User'}</p>
                  </div>

                  {/* Discord Roles Section */}
                  {user.discord_roles && user.discord_roles.length > 0 && (
                    <div className="px-5 pb-4">
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2 font-semibold">Rôles Discord</p>
                      <div className="flex flex-wrap gap-1.5">
                        {user.discord_roles.slice(0, 5).map((role, index) => {
                          // Use the hex color directly from the role
                          const color = role.color || '#99AAB5'; // Default Discord gray if no color
                          
                          return (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-semibold transition-all duration-200 hover:scale-105"
                              style={{
                                backgroundColor: `${color}20`,
                                color: color,
                                border: `1px solid ${color}40`
                              }}
                              title={role.name}
                            >
                              {role.name}
                            </span>
                          );
                        })}
                        {user.discord_roles.length > 5 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-semibold bg-zinc-800/50 text-gray-400 border border-zinc-700">
                            +{user.discord_roles.length - 5}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="px-3 pb-2">
                    {/* Referral Points Badge */}
                    <button
                      onClick={() => {
                        navigate('/parrainage');
                        setShowDropdown(false);
                      }}
                      className="w-full px-3 py-3 flex items-center justify-between bg-gradient-to-br from-cyan-500/[0.08] to-cyan-500/[0.02] border border-cyan-500/20 hover:border-cyan-400/50 hover:from-cyan-500/[0.14] rounded-xl transition-all group mb-1"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-9 h-9 bg-cyan-500/15 border border-cyan-400/30 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Gift className="w-4 h-4 text-cyan-300" strokeWidth={2.2} />
                        </div>
                        <div className="text-left min-w-0">
                          <p className="text-cyan-200 text-[13px] font-semibold leading-tight truncate">{t('header.referralPoints')}</p>
                          <p className="text-gray-500 text-[11px] mt-0.5 truncate">{t('header.clickToSeeRewards')}</p>
                        </div>
                      </div>
                      <div className="flex items-baseline gap-1 flex-shrink-0 ml-2 bg-cyan-500/10 border border-cyan-400/20 rounded-lg px-2.5 py-1">
                        <span className="text-base font-black text-cyan-300 tabular-nums">{referralPoints}</span>
                        <span className="text-cyan-500/70 text-[10px] font-medium">pts</span>
                      </div>
                    </button>

                    <button
                      onClick={() => {
                        navigate('/espace-client');
                        setShowDropdown(false);
                      }}
                      className="w-full px-3 py-2.5 flex items-center gap-3 text-gray-300 hover:bg-white/[0.04] hover:text-white rounded-lg transition-colors"
                      data-testid="client-space-link"
                    >
                      <Home className="w-4 h-4 text-gray-400" strokeWidth={2} />
                      <span className="text-[13px] font-medium">{t('sidebar.clientSpace')}</span>
                    </button>

                    <button
                      onClick={() => {
                        navigate('/mes-commandes');
                        setShowDropdown(false);
                      }}
                      className="w-full px-3 py-2.5 flex items-center gap-3 text-gray-300 hover:bg-white/[0.04] hover:text-white rounded-lg transition-colors"
                      data-testid="my-orders-link"
                    >
                      <Package className="w-4 h-4 text-gray-400" strokeWidth={2} />
                      <span className="text-[13px] font-medium">{t('sidebar.myOrders')}</span>
                    </button>

                    {hasLicence && (
                      <button
                        onClick={() => {
                          navigate('/ma-licence');
                          setShowDropdown(false);
                        }}
                        className="w-full px-3 py-2.5 flex items-center gap-3 text-indigo-300 hover:bg-indigo-500/10 hover:text-indigo-200 rounded-lg transition-colors"
                        data-testid="my-licence-link"
                      >
                        <Key className="w-4 h-4" strokeWidth={2} />
                        <span className="text-[13px] font-medium">Ma Licence</span>
                      </button>
                    )}

                    {isAffiliate && (
                      <button
                        onClick={() => {
                          navigate('/mon-affiliation');
                          setShowDropdown(false);
                        }}
                        className="w-full px-3 py-2.5 flex items-center gap-3 text-purple-300 hover:bg-purple-500/10 hover:text-purple-200 rounded-lg transition-colors"
                        data-testid="my-affiliate-link"
                      >
                        <TrendingUp className="w-4 h-4" strokeWidth={2} />
                        <span className="text-[13px] font-medium">Mon Affiliation</span>
                      </button>
                    )}

                    {isHelper && (
                      <button
                        onClick={() => {
                          navigate('/espace-helper');
                          setShowDropdown(false);
                        }}
                        className="w-full px-3 py-2.5 flex items-center justify-between text-cyan-300 hover:bg-cyan-500/10 rounded-lg transition-colors group"
                        data-testid="helper-space-link"
                      >
                        <div className="flex items-center gap-3">
                          <Wrench className="w-4 h-4 text-cyan-400" strokeWidth={2} />
                          <span className="text-[13px] font-medium">Espace Helper</span>
                        </div>
                        <span className="text-[9px] bg-cyan-500/20 text-cyan-300 px-1.5 py-0.5 rounded font-bold tracking-wider">HELPER</span>
                      </button>
                    )}

                    {isAdmin && (
                      <button
                        onClick={() => {
                          navigate('/admin');
                          setShowDropdown(false);
                        }}
                        className="w-full px-3 py-2.5 flex items-center justify-between text-cyan-300 hover:bg-cyan-500/10 rounded-lg transition-colors"
                        data-testid="admin-link"
                      >
                        <div className="flex items-center gap-3">
                          <Shield className="w-4 h-4 text-cyan-400" strokeWidth={2} />
                          <span className="text-[13px] font-medium">Panel Admin</span>
                        </div>
                        <span className="text-[9px] bg-cyan-500/20 text-cyan-300 px-1.5 py-0.5 rounded font-bold tracking-wider">ADMIN</span>
                      </button>
                    )}
                  </div>

                  <div className="border-t border-white/[0.06] px-3 py-2">
                    <button
                      onClick={() => {
                        logout();
                        setShowDropdown(false);
                      }}
                      className="w-full px-3 py-2.5 flex items-center gap-3 text-red-400 hover:bg-red-500/10 hover:text-red-300 rounded-lg transition-colors"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4" strokeWidth={2} />
                      <span className="text-[13px] font-semibold">{t('sidebar.logout')}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Button 
              onClick={loginWithDiscord}
              className="bg-[#5865F2] hover:bg-[#4752C4] text-white text-[9px] sm:text-sm font-medium px-2 sm:px-4 py-1 sm:py-2 rounded-lg transition-all duration-300 hover:scale-105 flex items-center gap-1 sm:gap-2"
              data-testid="login-discord-btn"
            >
              <svg className="w-3.5 h-3.5 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
              Login
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
